import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Send, Save, Eye } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg, money, fmtDate, datePrefix, today } from "@/lib/api";

const round2 = (n) => Math.round(n * 100) / 100;

export default function DraftEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [inv, setInv] = useState(null);
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get(`/invoices/${id}`).then((r) => {
      if (r.data.status !== "draft") return navigate(`/invoices/${id}`, { replace: true });
      setInv(r.data);
      setF({ items: r.data.line_items, tax: r.data.tax_rate, due_date: r.data.due_date || "", notes: r.data.notes || "" });
    }).catch((e) => { toast.error(errMsg(e)); navigate("/drafts"); });
  }, [id, navigate]);

  if (!inv || !f) return <div className="text-sm text-muted-foreground">Loading draft...</div>;

  const subtotal = round2(f.items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0));
  const taxAmt = round2(subtotal * (Number(f.tax) || 0) / 100);
  const total = round2(subtotal + taxAmt);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setItem = (i, k) => (e) => setF({ ...f, items: f.items.map((it, j) => (j === i ? { ...it, [k]: e.target.value } : it)) });
  const addItem = () => setF({ ...f, items: [...f.items, { description: datePrefix(today()), quantity: 1, unit_price: f.items[f.items.length - 1]?.unit_price ?? "" }] });
  const removeItem = (i) => setF({ ...f, items: f.items.filter((_, j) => j !== i) });

  const payload = () => {
    const items = f.items.filter((it) => String(it.description).trim());
    if (!items.length) throw new Error("Add at least one charge with a description");
    return {
      line_items: items.map((it) => ({ description: it.description.trim(), quantity: Number(it.quantity) || 1, unit_price: Number(it.unit_price) || 0 })),
      tax_rate: Number(f.tax) || 0, due_date: f.due_date || null, notes: f.notes,
    };
  };

  const save = async (thenSend) => {
    setBusy(thenSend ? "send" : "save");
    try {
      const r = await api.put(`/invoices/${id}`, payload());
      setInv(r.data);
      if (!thenSend) return toast.success("Draft saved");
      const sent = await api.post(`/invoices/${id}/send`);
      toast.success(`Invoice ${sent.data.invoice_number} sent to ${sent.data.sent_to}`);
      navigate(`/invoices/${id}`);
    } catch (e) { toast.error(errMsg(e) || e.message, { duration: 9000 }); } finally { setBusy(""); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this draft? The charges on it will be lost.")) return;
    setBusy("delete");
    try { await api.delete(`/invoices/${id}`); toast.success("Draft deleted"); navigate("/drafts"); }
    catch (e) { toast.error(errMsg(e)); setBusy(""); }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="draft-editor">
      <div className="flex items-center gap-3">
        <Link to="/drafts" data-testid="draft-back-link" className="h-9 w-9 grid place-items-center rounded-lg hover:bg-secondary"><ArrowLeft className="h-4 w-4" /></Link>
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-extrabold tracking-tight truncate" data-testid="draft-client-name">{inv.client?.name}</h1>
          <p className="text-xs text-muted-foreground"><span className="font-mono font-semibold">{inv.invoice_number}</span> · draft started {fmtDate(inv.date)} · dated the day you send it</p>
        </div>
      </div>

      <div className="surface p-4 sm:p-5 space-y-3">
        <div className="flex items-center justify-between">
          <Label>Charges</Label>
          <button type="button" data-testid="draft-add-line-button" onClick={addItem} className="text-xs font-bold text-emerald-600 hover:text-emerald-700 flex items-center gap-1">
            <Plus className="h-3.5 w-3.5" /> Add charge for today
          </button>
        </div>
        <div className="grid grid-cols-[1fr_56px_88px_32px] gap-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground px-1">
          <span>Description</span><span>Qty</span><span>Price</span><span />
        </div>
        {f.items.map((it, i) => (
          <div key={i} className="grid grid-cols-[1fr_56px_88px_32px] gap-2 items-center" data-testid={`draft-line-${i}`}>
            <Input data-testid={`draft-line-description-${i}`} value={it.description} onChange={setItem(i, "description")} placeholder="Sep 11 – Weekly mowing" />
            <Input data-testid={`draft-line-qty-${i}`} type="number" step="0.01" min="0" value={it.quantity} onChange={setItem(i, "quantity")} className="font-mono px-2" aria-label="Quantity" />
            <Input data-testid={`draft-line-price-${i}`} type="number" step="0.01" min="0" value={it.unit_price} onChange={setItem(i, "unit_price")} placeholder="0.00" className="font-mono px-2" aria-label="Price" />
            <button type="button" data-testid={`draft-line-remove-${i}`} onClick={() => removeItem(i)} disabled={f.items.length === 1}
              className="h-8 w-8 grid place-items-center rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 disabled:opacity-30" aria-label="Remove charge">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>

      <div className="surface p-4 sm:p-5 grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Tax %</Label>
          <Input data-testid="draft-tax-input" type="number" step="0.001" min="0" value={f.tax} onChange={set("tax")} className="font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label>Due date</Label>
          <Input data-testid="draft-due-date-input" type="date" value={f.due_date} onChange={set("due_date")} />
        </div>
        <div className="col-span-2 space-y-1.5">
          <Label>Notes on invoice</Label>
          <Textarea data-testid="draft-notes-input" rows={2} value={f.notes} onChange={set("notes")} />
        </div>
      </div>

      <div className="rounded-lg bg-secondary p-4 space-y-1 text-sm">
        <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono" data-testid="draft-subtotal">{money(subtotal)}</span></div>
        <div className="flex justify-between"><span className="text-muted-foreground">Tax ({Number(f.tax) || 0}%)</span><span className="font-mono">{money(taxAmt)}</span></div>
        <div className="flex justify-between items-baseline border-t border-border pt-2 mt-2">
          <span className="font-semibold">Total</span>
          <span className="font-mono font-bold text-2xl text-emerald-600" data-testid="draft-total">{money(total)}</span>
        </div>
      </div>

      {!inv.client?.email && <p className="text-xs text-amber-600 font-medium">This client has no email on file. Add one on the Clients tab to email this invoice.</p>}
      <div className="grid grid-cols-2 gap-3">
        <Button data-testid="draft-save-button" variant="outline" disabled={!!busy} onClick={() => save(false)} className="h-12 font-bold">
          <Save className="h-4 w-4 mr-2" /> {busy === "save" ? "Saving..." : "Save Draft"}
        </Button>
        <Button data-testid="draft-send-button" disabled={!!busy || !inv.client?.email} onClick={() => save(true)} className="zap-btn h-12 rounded-lg">
          <Send className="h-4 w-4 mr-2" /> {busy === "send" ? "Sending..." : "Email Invoice"}
        </Button>
      </div>
      <div className="flex items-center justify-between">
        <Link to={`/invoices/${id}`} data-testid="draft-preview-link" className="text-sm font-semibold text-muted-foreground hover:text-foreground flex items-center gap-1.5"><Eye className="h-4 w-4" /> Preview / PDF</Link>
        <Button data-testid="draft-delete-button" variant="ghost" disabled={!!busy} onClick={remove} className="text-red-600 hover:text-red-700"><Trash2 className="h-4 w-4 mr-2" /> Delete draft</Button>
      </div>
    </div>
  );
}
